const GENERIC_STEP_TITLE_PATTERN = /^\s*(?:step|phase|stage|шаг|крок|paso|etapa|passo|pas|pasul)\s*[\divxlcdm]+(?:\b|[\s.)-])/i;
const NUMERIC_TITLE_PATTERN = /^\s*\d+[\s.)-]*$/;
const PARENTHESIZED_TEXT_PATTERN = /\(([^)]{1,48})\)/;
const TIME_TEXT_PATTERN = /(?:\d+\s*(?:min|mins|minute|minutes|m|h|hr|hrs|hour|hours|мин|минут|хв|ч|час|hora|horas|ore|ora)|overnight|night|noche|nit|veille|noapte|notte|ноч|ніч)/i;
const THAW_TEXT_PATTERN = /thaw|defrost|freezer|congelador|descongel|морозил|размороз|розмороз|congelateur|congelator|scongel/i;
const OVERNIGHT_THAW_PATTERN = /overnight|night before|toda la noche|noche anterior|tota la nit|nit abans|toute la nuit|la veille|peste noapte|tutta la notte|sera prima|на ночь|ночью|на ніч|переложите в холодильник на ночь|холодильник на ночь/i;

const toDetailsArray = (step) => {
  const candidates = [step?.details, step?.substeps, step?.instructions, step?.steps];
  const value = candidates.find(candidate => candidate !== undefined && candidate !== null);

  if (Array.isArray(value)) {
    return value.map(item => String(item || '').trim()).filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }

  return [];
};

const getStepTitle = (step) => String(step?.step_title || step?.title || '').trim();

const getStepText = (step) => {
  if (typeof step === 'string') return step;
  return `${getStepTitle(step)} ${toDetailsArray(step).join(' ')}`.trim();
};

const hasTimedTitle = (title) => {
  const paren = String(title || '').match(PARENTHESIZED_TEXT_PATTERN);
  return Boolean(paren && TIME_TEXT_PATTERN.test(paren[1]));
};

const isGenericTitle = (title) => {
  const value = String(title || '').trim();
  return !value || NUMERIC_TITLE_PATTERN.test(value) || GENERIC_STEP_TITLE_PATTERN.test(value);
};

const isThawStep = (step) => THAW_TEXT_PATTERN.test(getStepText(step));

export const normalizeCookingInstructionsShape = (instructions) => {
  if (!instructions || typeof instructions !== 'object') return instructions;
  if (!Array.isArray(instructions.cooking_instructions)) return instructions;

  instructions.cooking_instructions = instructions.cooking_instructions.map((step) => {
    if (typeof step === 'string') {
      return { step_title: '', details: [step.trim()].filter(Boolean) };
    }

    if (!step || typeof step !== 'object') {
      return { step_title: '', details: [] };
    }

    return {
      ...step,
      step_title: getStepTitle(step),
      details: toDetailsArray(step),
    };
  });

  return instructions;
};

export const validateCookingInstructions = (instructions) => {
  const issues = [];

  if (!instructions || typeof instructions !== 'object') {
    return { isValid: false, issues: ['recipe details response is not an object'] };
  }

  normalizeCookingInstructionsShape(instructions);

  if (instructions._partial) {
    issues.push('recipe details response was partial or repaired from truncated JSON');
  }

  const steps = instructions.cooking_instructions;
  if (!Array.isArray(steps) || steps.length === 0) {
    issues.push('cooking_instructions is missing or empty');
    return { isValid: false, issues };
  }

  if (steps.length < 2) {
    issues.push('cooking_instructions has fewer than 2 grouped phases');
  }

  const genericTitles = [];
  const missingTimedTitles = [];
  const emptyDetails = [];
  const overnightThawSteps = [];

  steps.forEach((step, index) => {
    const title = getStepTitle(step);
    const label = title || `step ${index + 1}`;

    if (isGenericTitle(title)) genericTitles.push(label);
    if (!hasTimedTitle(title)) missingTimedTitles.push(label);
    if (toDetailsArray(step).length === 0) emptyDetails.push(label);
    if (isThawStep(step) && OVERNIGHT_THAW_PATTERN.test(getStepText(step))) overnightThawSteps.push(label);
  });

  if (genericTitles.length > 0) {
    issues.push(`generic step_title values: ${genericTitles.slice(0, 3).join(', ')}`);
  }

  if (missingTimedTitles.length > 0) {
    issues.push(`step_title values missing time in parentheses: ${missingTimedTitles.slice(0, 3).join(', ')}`);
  }

  if (emptyDetails.length > 0) {
    issues.push(`steps with empty details arrays: ${emptyDetails.slice(0, 3).join(', ')}`);
  }

  if (overnightThawSteps.length > 0) {
    issues.push(`thaw steps ask the user to wait overnight instead of thawing now: ${overnightThawSteps.slice(0, 3).join(', ')}`);
  }

  const nonThawSteps = steps.filter(step => !isThawStep(step));
  const singleDetailSteps = nonThawSteps.filter(step => toDetailsArray(step).length <= 1);
  const hasGroupedDetails = nonThawSteps.some(step => toDetailsArray(step).length >= 2);

  if (nonThawSteps.length >= 2 && singleDetailSteps.length >= Math.ceil(nonThawSteps.length * 0.75)) {
    issues.push('too many non-thaw phases have only one detail; group related actions under 2-4 details');
  }

  if (nonThawSteps.length > 0 && !hasGroupedDetails) {
    issues.push('no non-thaw phase contains grouped substeps');
  }

  return { isValid: issues.length === 0, issues };
};

export const createCookingInstructionsRetryPrompt = (basePrompt, issues = []) => `${basePrompt}

COOKING_INSTRUCTIONS QUALITY RETRY (CRITICAL):
The previous response would be rejected for: ${issues.join('; ') || 'invalid cooking instruction structure'}.
Return the full JSON object again. Keep updated_title, visual_description, full_description, storage, tips, and equipment concise if needed, but DO NOT omit cooking_instructions.
The cooking_instructions array must use meaningful localized phase titles with time in parentheses, never generic numbered titles.
Each non-thaw phase must contain 2-4 grouped detail sentences. A thaw phase may have one detail but still needs a timed title.
For freezer fish/seafood, thaw now: keep it sealed, submerge in cold water for 20-30 min, then cook immediately. Name the actual frozen ingredient (shrimp/prawns/salmon/mussels), not generic fish. Never tell the user to thaw overnight.
Output only valid JSON. No markdown.`;